/**
 * Effort, as a signal-strength meter.
 *
 * Round 7, owner: "Same as effort level, make it bar style like Conductor."
 * Five ascending bars filled to the level and the level's name beside them.
 * (The lightning mark that used to lead it is gone, 2026-09-10: a bolt means
 * fast mode in the tools this borrows from, and effort is not that.) Our five
 * levels (low, medium, high, xhigh, max) land one to one on five bars, which
 * is the whole reason the shape fits.
 *
 * It is a SLIDER, not a picture: `role="slider"` over six stops, where stop 0
 * is "follow the default" and 1..5 are the levels. That keeps the behaviour
 * the old select had (null clears the override back to inherited) while
 * giving the control real semantics: arrow keys move a stop, Home is the
 * default, End is max, the digits pick a level outright, and a screen reader
 * reads `aria-valuetext` ("High effort", "Default (medium effort)") rather
 * than a number nobody can interpret.
 *
 * Inherited and chosen look different on purpose: an inherited level draws
 * its bars in the muted text colour, a chosen one in the accent. Otherwise
 * "medium because I picked it" and "medium because my account says so" would
 * be the same picture.
 *
 * A step shows immediately and tells the caller at once (writing an effort
 * costs nothing but a column write). The optimistic value is dropped again
 * after a short settle, so a write the server refuses snaps back to the truth
 * instead of leaving a level on screen that was never saved.
 */

import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

import type { Effort } from '../../api/types';
import { EFFORT_ORDER, effortDisplayName } from './labels';

/**
 * The backstop: how long an optimistic step is trusted when the write never
 * lands. A write that DOES land drops it the moment it does (the check in
 * render below), so when this fires it either snaps back a refused write or
 * finds nothing left to drop.
 */
export const EFFORT_SETTLE_MS = 2500;

/** Stop 0 is "follow the default"; 1..5 are the levels, weakest first. */
export const EFFORT_STOPS = EFFORT_ORDER.length;

/** The stop a value sits on: null (inherit) is 0. */
export function effortStop(effort: Effort | null): number {
  if (effort === null) return 0;
  const at = EFFORT_ORDER.indexOf(effort);
  return at === -1 ? 0 : at + 1;
}

/** The value a stop means: 0 clears the override back to inherited. */
export function effortAtStop(stop: number): Effort | null {
  return EFFORT_ORDER[stop - 1] ?? null;
}

/** Bar heights, shortest to tallest: the meter reads as a ramp. */
const BAR_HEIGHTS = ['h-1', 'h-1.5', 'h-2', 'h-2.5', 'h-3'];

export function EffortMeter({
  effort,
  defaultEffort,
  onChange,
  disabled = false,
  disabledReason,
  testId,
  ariaLabel = 'Effort for this conversation',
  inheritWord = 'Default',
}: {
  /** the chosen override, or null for "inherit the default" */
  effort: Effort | null;
  /** what "Default" resolves to right now */
  defaultEffort: Effort;
  onChange: (effort: Effort | null) => void;
  disabled?: boolean;
  /** what to say instead of the usual tooltip while disabled */
  disabledReason?: string;
  testId: string;
  /** what this meter sets (the composer's is the conversation's) */
  ariaLabel?: string;
  /** the word for stop 0: "Default" in a composer, "Account setting" on a product */
  inheritWord?: string;
}) {
  const [pending, setPending] = useState<number | null>(null);
  const settleRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (settleRef.current !== null) window.clearTimeout(settleRef.current);
    },
    [],
  );

  const committed = effortStop(effort);
  const stop = pending ?? committed;

  // The write landed: stop guessing and read the row. Only when the truth
  // arrives at the level we ASKED for, so a slow first write cannot drag the
  // meter backwards past a second step the reader has already made. Done in
  // render (React re-runs it at once) rather than in an effect, so the meter
  // never commits a frame it is about to take back; the settle timer is left
  // to fire and find nothing to drop.
  if (pending !== null && committed === pending) setPending(null);

  const inherited = stop === 0;
  /** How many bars are lit: an inherited meter shows the level it inherits. */
  const filled = inherited ? effortStop(defaultEffort) : stop;
  const level = inherited ? defaultEffort : (effortAtStop(stop) ?? defaultEffort);
  const valueText = inherited
    ? `${inheritWord} (${effortDisplayName(defaultEffort).toLowerCase()} effort)`
    : `${effortDisplayName(level)} effort`;

  const step = (next: number) => {
    const clamped = Math.min(EFFORT_STOPS, Math.max(0, next));
    if (clamped === stop) return;
    setPending(clamped);
    onChange(effortAtStop(clamped));
    if (settleRef.current !== null) window.clearTimeout(settleRef.current);
    settleRef.current = window.setTimeout(() => {
      settleRef.current = null;
      setPending(null);
    }, EFFORT_SETTLE_MS);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'PageUp') {
      e.preventDefault();
      step(stop + 1);
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown' || e.key === 'PageDown') {
      e.preventDefault();
      step(stop - 1);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      step(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      step(EFFORT_STOPS);
      return;
    }
    if (/^[0-5]$/.test(e.key)) {
      e.preventDefault();
      step(Number(e.key));
    }
  };

  const title =
    disabled && disabledReason !== undefined
      ? disabledReason
      : `How hard the guide works per answer. Higher digs deeper and costs more. Now: ${valueText}. Click to step up, arrow keys to move, Home to follow the default`;

  return (
    <button
      type="button"
      role="slider"
      data-testid={testId}
      data-stop={stop}
      data-filled={filled}
      data-inherited={inherited}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={EFFORT_STOPS}
      aria-valuenow={stop}
      aria-valuetext={valueText}
      onClick={() => step(stop === EFFORT_STOPS ? 0 : stop + 1)}
      onKeyDown={onKeyDown}
      className={`flex h-7 min-w-0 cursor-pointer items-center gap-1.5 rounded-pill border-none bg-transparent px-2 text-[length:var(--text-ui-size)] transition-colors duration-(--motion-hover) hover:bg-inset hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent ${
        inherited ? 'text-tertiary' : 'text-secondary'
      }`}
    >
      <span aria-hidden className="flex h-3 shrink-0 items-end gap-[2px]">
        {BAR_HEIGHTS.map((height, i) => (
          <span
            key={height}
            className={`block w-[3px] rounded-pill ${height} ${
              i < filled
                ? inherited
                  ? 'bg-(--text-tertiary)'
                  : 'bg-(--accent)'
                : 'bg-(--border-default)'
            }`}
          />
        ))}
      </span>
      <span className="min-w-0 truncate">{effortDisplayName(level)}</span>
    </button>
  );
}
